import { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Brain, RefreshCw, BarChart2, Info } from 'lucide-react';
import { Metrics } from '../types';
import { calculateQuillMetrics } from '../../../../lib/editor-metrics';
import { supabaseClient } from '../../../../lib/supabase';
import { UniquenessDetailsModal } from './UniquenessDetailsModal';

// Expose recalcMetrics method via ref
export interface LeftSidebarRef {
  recalcMetrics: () => void;
}

interface LeftSidebarProps {
  metrics: Metrics;
  uniquenessScore: number;
  isRefreshingUniqueness: boolean;
  onRefreshUniqueness: () => Promise<void>;
  uniquenessUrl?: string | null;
  uniquenessMatches?: Record<string, string>;
  uniquenessIgnored?: string[];
  uniquenessReportUrl?: string | null;
  editorContent?: string;
  quillInstance?: any;
  analysisId?: string;
  onTextScoreChange?: (score: number) => void;
}

const LeftSidebar = forwardRef<LeftSidebarRef, LeftSidebarProps>(function LeftSidebar({
  metrics,
  uniquenessScore,
  isRefreshingUniqueness,
  onRefreshUniqueness,
  uniquenessUrl,
  uniquenessMatches = {},
  uniquenessIgnored = [],
  uniquenessReportUrl = null,
  editorContent = '',
  quillInstance,
  analysisId,
  onTextScoreChange
}, ref) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [contentMetrics, setContentMetrics] = useState<{
    wordCount: number;
    avgWordCount: number;
    maxWordCount: number;
    headers: {
      h2: { current: number; avg: number; max: number };
      h3: { current: number; avg: number; max: number };
      h4: { current: number; avg: number; max: number };
    };
  }>({
    wordCount: 0,
    avgWordCount: metrics.wordCount.avg || 0,
    maxWordCount: metrics.wordCount.max || 0,
    headers: {
      h2: { current: 0, avg: metrics.headers.h2.avg || 0, max: metrics.headers.h2.max || 0 },
      h3: { current: 0, avg: metrics.headers.h3.avg || 0, max: metrics.headers.h3.max || 0 },
      h4: { current: 0, avg: metrics.headers.h4.avg || 0, max: metrics.headers.h4.max || 0 }
    }
  });
  const [error, setError] = useState<string | null>(null);
  const [keywordCoverage, setKeywordCoverage] = useState<{
    present: number;
    total: number;
    score: number;
  }>({ present: 0, total: 0, score: 0 });

  useEffect(() => {
    setContentMetrics(prev => ({
      ...prev,
      avgWordCount: metrics.wordCount.avg || prev.avgWordCount,
      maxWordCount: metrics.wordCount.max || prev.maxWordCount,
      headers: {
        h2: { 
          ...prev.headers.h2, 
          avg: metrics.headers.h2.avg || prev.headers.h2.avg, 
          max: metrics.headers.h2.max || prev.headers.h2.max 
        },
        h3: { 
          ...prev.headers.h3, 
          avg: metrics.headers.h3.avg || prev.headers.h3.avg, 
          max: metrics.headers.h3.max || prev.headers.h3.max 
        },
        h4: { 
          ...prev.headers.h4, 
          avg: metrics.headers.h4.avg || prev.headers.h4.avg, 
          max: metrics.headers.h4.max || prev.headers.h4.max 
        }
      }
    }));
  }, [metrics]);

  // Метод для явного перерахунку метрик
  const recalcMetrics = useCallback(async () => {
    console.log('[LeftSidebar] recalcMetrics called', { 
      hasQuillInstance: !!quillInstance, 
      analysisId 
    });
    
    try {
      if (!quillInstance || !analysisId) {
        console.log('[LeftSidebar] Skipping recalc - missing quillInstance or analysisId');
        return;
      }
      const editorMetrics = calculateQuillMetrics(quillInstance);
      const currentText = quillInstance.getText().toLowerCase();
      const { data: analysis } = await supabaseClient
        .from('analyses')
        .select('keyword_analysis')
        .eq('id', analysisId)
        .single();
      if (analysis?.keyword_analysis?.keywords?.length) {
        const keywords = analysis.keyword_analysis.keywords;
        const significantKeywords = keywords.filter((k: any) => {
          // Older rows saved by KeywordComparisonTable only carry competitorCount.
          const competitorsCount = k.competitorsCount ?? k.competitorCount ?? 0;
          const competitorPercentage = competitorsCount / Math.max(1, k.totalCompetitors);
          const hasSufficientDensity = k.avgDensity >= 0.2;
          return competitorPercentage >= 0.5 && hasSufficientDensity;
        });
        const presentKeywords = significantKeywords.filter((keyword: any) => 
          currentText.includes(keyword.keyword.toLowerCase())
        );
        const totalImportance = significantKeywords.reduce((sum: number, k: any) => sum + k.importance, 0);
        const presentImportance = presentKeywords.reduce((sum: number, k: any) => sum + k.importance, 0);
        const coverageScore = totalImportance > 0 
          ? Math.round((presentImportance / totalImportance) * 100)
          : 0;
        setKeywordCoverage({
          present: presentKeywords.length,
          total: significantKeywords.length,
          score: coverageScore
        });
      }
      setContentMetrics(prev => ({
        ...prev,
        wordCount: editorMetrics.wordCount,
        headers: {
          h2: { ...prev.headers.h2, current: editorMetrics.headers.h2 },
          h3: { ...prev.headers.h3, current: editorMetrics.headers.h3 },
          h4: { ...prev.headers.h4, current: editorMetrics.headers.h4 }
        }
      }));
      const completionPercentage = calculateCompletionPercentage();
      onTextScoreChange?.(completionPercentage);
    } catch (err) {
      console.error('Error calculating metrics:', err);
    }
  }, [quillInstance, analysisId, onTextScoreChange]);

  // expose recalcMetrics via ref
  useImperativeHandle(ref, () => ({
    recalcMetrics
  }), [recalcMetrics]);

  useEffect(() => {
    if (quillInstance) {
      // Calculate metrics immediately on mount
      recalcMetrics();
      // Also calculate after short delays to ensure content is fully loaded
      const timeoutId1 = setTimeout(() => {
        recalcMetrics();
      }, 200);
      const timeoutId2 = setTimeout(() => {
        recalcMetrics();
      }, 500);
      const timeoutId3 = setTimeout(() => {
        recalcMetrics();
      }, 1000);
      const observer = new MutationObserver(recalcMetrics);
      observer.observe(quillInstance.root, {
        childList: true,
        subtree: true,
        characterData: true
      });
      return () => {
        clearTimeout(timeoutId1);
        clearTimeout(timeoutId2);
        clearTimeout(timeoutId3);
        observer.disconnect();
      };
    }
  }, [quillInstance, recalcMetrics]);

  const getComparisonColor = (current: number, avg: number) => {
    if (current >= avg) return 'text-green-600';
    if (current >= avg * 0.7) return 'text-yellow-600';
    return 'text-red-600';
  };

  const calculateCompletionPercentage = () => {
    const wordCountScore = contentMetrics.avgWordCount > 0 
      ? Math.min(100, Math.round((contentMetrics.wordCount / contentMetrics.avgWordCount) * 100))
      : 0;

    const keywordScore = keywordCoverage.score;

    if (keywordCoverage.total > 0) {
      return Math.round((wordCountScore * 0.6) + (keywordScore * 0.4));
    }
    
    return wordCountScore;
  };

  const completionPercentage = calculateCompletionPercentage();

  const getCompletionColor = () => {
    if (completionPercentage >= 75) return 'text-green-600';
    if (completionPercentage >= 50) return 'text-indigo-600';
    return 'text-yellow-600';
  };

  const circleRadius = 40;
  const circleCircumference = 2 * Math.PI * circleRadius;
  const circleDashArray = (completionPercentage / 100) * circleCircumference;
  const circleRemainder = circleCircumference - circleDashArray;

  return (
    <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
      <div className="p-4 border-b border-gray-200">
        <div className="text-center">
          <div className="relative w-32 h-32 mx-auto">
            <svg className="w-full h-full" viewBox="0 0 100 100">
              <circle 
                cx="50" 
                cy="50" 
                r={circleRadius} 
                fill="none" 
                stroke="#f3f4f6" 
                strokeWidth="8"
              />
              <circle 
                cx="50" 
                cy="50" 
                r={circleRadius} 
                fill="none" 
                stroke={
                  completionPercentage >= 75 ? '#10b981' : 
                  completionPercentage >= 50 ? '#4f46e5' : 
                  '#eab308'
                } 
                strokeWidth="8"
                strokeDasharray={`${circleDashArray} ${circleRemainder}`}
                strokeDashoffset="0"
                strokeLinecap="round"
                transform="rotate(-90 50 50)"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className={`text-3xl font-bold ${getCompletionColor()}`}>
                {completionPercentage}%
              </span>
            </div>
          </div>
          <p className="mt-2 text-sm font-bold text-gray-700">Text Score</p>
          {keywordCoverage.total > 0 && (
            <p className="mt-1 text-xs text-gray-500">
              Keywords: {keywordCoverage.present}/{keywordCoverage.total} ({keywordCoverage.score}%)
            </p>
          )}
        </div>
      </div>

      <div className="p-4 border-b border-gray-200">
        <h3 className="text-sm font-medium text-gray-700 mb-4">Metrics (Current/Avg/Max)</h3>
        
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Word Count</span>
            <span className="text-sm">
              <span className={getComparisonColor(contentMetrics.wordCount, contentMetrics.avgWordCount)}>
                {contentMetrics.wordCount}
              </span>
              <span className="text-gray-400">/</span>
              <span className="text-indigo-600">{contentMetrics.avgWordCount}</span>
              <span className="text-gray-400">/</span>
              <span className="text-green-600">{contentMetrics.maxWordCount}</span>
            </span>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">H2</span>
              <span className="text-sm">
                <span className={getComparisonColor(contentMetrics.headers.h2.current, contentMetrics.headers.h2.avg)}>
                  {contentMetrics.headers.h2.current}
                </span>
                <span className="text-gray-400">/</span>
                <span className="text-indigo-600">{contentMetrics.headers.h2.avg}</span>
                <span className="text-gray-400">/</span>
                <span className="text-green-600">{contentMetrics.headers.h2.max}</span>
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">H3</span>
              <span className="text-sm">
                <span className={getComparisonColor(contentMetrics.headers.h3.current, contentMetrics.headers.h3.avg)}>
                  {contentMetrics.headers.h3.current}
                </span>
                <span className="text-gray-400">/</span>
                <span className="text-indigo-600">{contentMetrics.headers.h3.avg}</span>
                <span className="text-gray-400">/</span>
                <span className="text-green-600">{contentMetrics.headers.h3.max}</span>
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">H4</span>
              <span className="text-sm">
                <span className={getComparisonColor(contentMetrics.headers.h4.current, contentMetrics.headers.h4.avg)}>
                  {contentMetrics.headers.h4.current}
                </span>
                <span className="text-gray-400">/</span>
                <span className="text-indigo-600">{contentMetrics.headers.h4.avg}</span>
                <span className="text-gray-400">/</span>
                <span className="text-green-600">{contentMetrics.headers.h4.max}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">Text Uniqueness:</span>
            <span className="text-sm font-semibold text-gray-900">{uniquenessScore}%</span>
            {uniquenessScore > 0 && Object.keys(uniquenessMatches).length > 0 && (
              <button
                onClick={() => setIsModalOpen(true)}
                className="p-1 hover:bg-gray-100 rounded-full"
                title="View detailed report"
              >
                <Info className="h-4 w-4 text-blue-600" />
              </button>
            )}
          </div>
          <button
            onClick={() => {
              console.log('Uniqueness button clicked in LeftSidebar');
              onRefreshUniqueness();
            }}
            disabled={isRefreshingUniqueness}
            className="p-1 hover:bg-gray-100 rounded-full disabled:opacity-50"
            title="Check text uniqueness"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshingUniqueness ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <UniquenessDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        percentage={uniquenessScore}
        matches={uniquenessMatches}
        ignored={uniquenessIgnored}
        reportUrl={uniquenessReportUrl}
      />
    </div>
  );
}
);
export { LeftSidebar };